import {
  GuideTargetsAvailablePeriodsDto,
  UpdateRevenueTarget,
} from '@act/shared/data-transfer-objects';
import {
  GuideTargetsUpdatedEventPayload,
  GuideTargetsUpdatedTypes,
} from '@act/shared/socket-events';
import {
  createAction,
  createSelector,
  createSlice,
  type PayloadAction,
} from '@reduxjs/toolkit';
import { useAppSelector } from 'redux/storeHooks';
import {
  defaultGuideTargetsFilters,
  type GuideTargetsFiltersType,
} from 'redux/types/guideTargetsTypes.util';
import { createApiThunk } from 'redux/utils/apiThunk';
import { addToEffectsMiddleware } from 'redux/utils/effectsMiddleware.util';
import { resetRedux } from 'redux/actions/sharedActions';
import type { RootState } from 'redux/reemaStore.util';
import type { WebsocketEventActions } from 'redux/types/websocketTypes';
import type { GuideTargetsState } from '../types/guideTargetsTypes.util';
import { guideTargetsRequests } from 'redux/requests/guideTargetsRequests';
import { PersistConfig, persistReducer } from 'redux-persist';
import localStorage from 'redux-persist/lib/storage';
import autoMergeLevel1 from 'redux-persist/es/stateReconciler/autoMergeLevel1';

export const getPaginatedGuideTargets = createApiThunk(
  guideTargetsRequests.getGuideTargets
);

export const getGuideTargetsAvailablePeriods = createApiThunk(
  guideTargetsRequests.getGuideTargetsAvailablePeriods
);

export const getGuideTargetTypes = createApiThunk(
  guideTargetsRequests.getGuideTargetTypes
);

export const getGuideTargetsServiceAreas = createApiThunk(
  guideTargetsRequests.getGuideTargetsServiceAreas
);

export const updateClientRevenueSuccess = createAction<UpdateRevenueTarget>(
  'guideTargets/updateClientRevenueSuccess'
);

const initialState: GuideTargetsState = {
  status: 'initial',
  latestPagedGuideTargetsRequestId: null,
  items: [],
  count: 0,
  targetTypeKeys: [],
  totalQueued: 0,
  totalsByTargetType: [],
  filters: defaultGuideTargetsFilters(),
  sideNavClientId: null,
  selectedYearMo: null,
  availablePeriodsStatus: 'initial',
  availableYearMoValues: [],
  targetsUpdated: 0,
  targetTypes: [],
  serviceAreas: [],
  targetTypesStatus: 'initial',
  serviceAreasStatus: 'initial',
  clientRevenue: null,
};

export const guideTargetsSlice = createSlice({
  name: 'guideTargets',
  initialState,
  reducers: {
    setGuideTargetsFilters: (
      state: GuideTargetsState,
      action: PayloadAction<Partial<GuideTargetsFiltersType>>
    ) => {
      state.filters = { ...state.filters, ...action.payload };
    },
    resetGuideTargetsFilters: (state: GuideTargetsState) => {
      state.filters = defaultGuideTargetsFilters();
    },
    setGuideTargetsSideNavClientId: (
      state: GuideTargetsState,
      action: PayloadAction<string | null>
    ) => {
      state.sideNavClientId = action.payload;
    },
    setGuideTargetsSelectedYearMo: (
      state: GuideTargetsState,
      action: PayloadAction<number | null>
    ) => {
      state.selectedYearMo = action.payload;
    },
    clearGuideTargetsPagedResult: (state: GuideTargetsState) => {
      state.status = 'initial';
      state.latestPagedGuideTargetsRequestId = null;
      state.items = [];
      state.count = 0;
      state.targetTypeKeys = [];
      state.totalQueued = 0;
      state.totalsByTargetType = [];
    },
    guideTargetsUpdatedFromSocket: (
      state: GuideTargetsState,
      action: PayloadAction<GuideTargetsUpdatedEventPayload>
    ) => {
      const { yearMo } = action.payload ?? {};
      if (
        state.selectedYearMo != null &&
        (yearMo === undefined || yearMo === state.selectedYearMo)
      ) {
        state.targetsUpdated += 1;
      }
    },
  },
  extraReducers: builder => {
    builder
      .addCase(resetRedux, () => ({ ...initialState }))
      .addCase(getGuideTargetsAvailablePeriods.pending, state => {
        state.availablePeriodsStatus = 'loading';
      })
      .addCase(
        getGuideTargetsAvailablePeriods.fulfilled,
        (state, action: PayloadAction<GuideTargetsAvailablePeriodsDto>) => {
          state.availablePeriodsStatus = 'success';
          state.availableYearMoValues = action.payload.yearMoValues;
        }
      )
      .addCase(getGuideTargetsAvailablePeriods.rejected, state => {
        state.availablePeriodsStatus = 'failed';
        state.availableYearMoValues = [];
      })
      .addCase(getPaginatedGuideTargets.pending, (state, action) => {
        state.status = 'loading';
        state.latestPagedGuideTargetsRequestId = action.meta.requestId;
      })
      .addCase(getPaginatedGuideTargets.fulfilled, (state, action) => {
        if (action.meta.requestId !== state.latestPagedGuideTargetsRequestId) {
          return;
        }
        const payload = action.payload;
        state.status = 'success';
        state.items = payload.items ?? [];
        state.count = payload.count ?? 0;
        state.totalQueued = payload.totalQueued ?? 0;
        state.targetTypeKeys = payload.targetTypeKeys ?? [];
        state.totalsByTargetType = payload.totalsByTargetType ?? [];
        state.clientRevenue = payload.clientRevenue ?? null;
      })
      .addCase(getPaginatedGuideTargets.rejected, (state, action) => {
        if (action.meta.requestId !== state.latestPagedGuideTargetsRequestId) {
          return;
        }
        state.status = 'failed';
        state.items = [];
      })
      .addCase(getGuideTargetTypes.pending, state => {
        state.targetTypesStatus = 'loading';
      })
      .addCase(getGuideTargetTypes.fulfilled, (state, action) => {
        state.targetTypesStatus = 'success';
        state.targetTypes = action.payload;
      })
      .addCase(getGuideTargetTypes.rejected, state => {
        state.targetTypesStatus = 'failed';
        state.targetTypes = [];
      })
      .addCase(getGuideTargetsServiceAreas.pending, state => {
        state.serviceAreasStatus = 'loading';
      })
      .addCase(getGuideTargetsServiceAreas.fulfilled, (state, action) => {
        state.serviceAreasStatus = 'success';
        state.serviceAreas = action.payload;
      })
      .addCase(getGuideTargetsServiceAreas.rejected, state => {
        state.serviceAreasStatus = 'failed';
        state.serviceAreas = [];
      })
      .addCase(
        updateClientRevenueSuccess,
        (state, action: PayloadAction<UpdateRevenueTarget>) => {
          const serviceArea = state.clientRevenue?.byServiceArea.find(
            s => s.serviceAreaKey === action.payload.serviceAreaKey
          );
          if (serviceArea) {
            serviceArea.revenueTarget = action.payload.revenueTarget;
          }
        }
      );
  },
});

export const {
  setGuideTargetsFilters,
  resetGuideTargetsFilters,
  setGuideTargetsSideNavClientId,
  setGuideTargetsSelectedYearMo,
  clearGuideTargetsPagedResult,
  guideTargetsUpdatedFromSocket,
} = guideTargetsSlice.actions;

const persistConfig: PersistConfig<GuideTargetsState> = {
  key: 'guide_targets',
  storage: localStorage,
  whitelist: ['filters', 'selectedYearMo', 'sideNavClientId'],
  stateReconciler: autoMergeLevel1,
  version: 3,
};

export const guideTargetsReducer = persistReducer(
  persistConfig,
  guideTargetsSlice.reducer
);

const selectGuideTargetsState = (state: RootState) => state.guideTargets;

const selectGuideTargetsFiltersState = createSelector(
  selectGuideTargetsState,
  s => s?.filters ?? defaultGuideTargetsFilters()
);

const selectGuideTargetsItems = createSelector(
  selectGuideTargetsState,
  s => s?.items ?? []
);

const selectGuideTargetsCount = createSelector(
  selectGuideTargetsState,
  s => s?.count ?? 0
);

const selectGuideTargetsStatus = createSelector(
  selectGuideTargetsState,
  s => s?.status ?? 'initial'
);

//We dont want to show the reengagement target type in the UI
const selectTargetTypes = createSelector(
  selectGuideTargetsState,
  s => (s?.targetTypes ?? []).filter(t => t.key !== 'reengagement') ?? []
);

// We only want to show SC service areas in the UI
const selectOnlyScServiceAreaKeys = createSelector(selectGuideTargetsState, s =>
  (s?.serviceAreas ?? []).filter(area => area.name.startsWith('SC'))
);

const selectGuideTargetsSelectedYearMo = createSelector(
  selectGuideTargetsState,
  s => s?.selectedYearMo ?? null
);

const selectGuideTargetsSideNavClientId = createSelector(
  selectGuideTargetsState,
  s => s?.sideNavClientId ?? null
);

const selectGuideTargetsTotalQueued = createSelector(
  selectGuideTargetsState,
  s => s?.totalQueued ?? 0
);

const selectGuideTargetsTotalsByTargetType = createSelector(
  selectGuideTargetsState,
  s => s?.totalsByTargetType ?? []
);

const selectAvailablePeriodsStatus = createSelector(
  selectGuideTargetsState,
  s => s?.availablePeriodsStatus ?? 'initial'
);

const selectAvailableYearMoValues = createSelector(
  selectGuideTargetsState,
  s => s?.availableYearMoValues ?? []
);

const selectGuideTargetsTargetsUpdated = createSelector(
  selectGuideTargetsState,
  s => s?.targetsUpdated ?? 0
);

const selectTargetTypeKeys = createSelector(
  selectGuideTargetsState,
  s => s?.targetTypeKeys.filter(key => key !== 'reengagement') ?? []
);

const currentClientRevenue = (state: GuideTargetsState) => {
  if (state.clientRevenue?.byServiceArea?.length > 0) {
    if (
      state.filters.clientIds?.length > 1 ||
      state.filters?.serviceAreaKeys?.length > 1
    ) {
      return null;
    }
    if (
      state.clientRevenue?.byServiceArea?.length === 1 &&
      state.filters?.serviceAreaKeys?.length === 0
    ) {
      return state.clientRevenue?.byServiceArea?.[0];
    }
    if (
      state.filters.clientIds?.length <= 1 &&
      state.filters?.serviceAreaKeys?.length <= 1
    ) {
      return state.clientRevenue?.byServiceArea?.find(
        c => c.serviceAreaKey === state.filters.serviceAreaKeys[0]
      );
    }
  }
  return null;
};

const selectCurrentClientRevenue = createSelector(selectGuideTargetsState, s =>
  s ? currentClientRevenue(s) : null
);

const selectUnAllocatedRevenue = createSelector(selectGuideTargetsState, s => {
  const current = s ? currentClientRevenue(s) : null;
  return (
    (current?.revenueTarget ?? 0) -
    (s?.totalsByTargetType.find(t => t.targetTypeKey === 'total_engagement')
      ?.total ?? 0)
  );
});

addToEffectsMiddleware(addEffect => {
  addEffect({
    action: guideTargetsUpdatedFromSocket,
    effect: async ({ dispatch }) => {
      void dispatch(getGuideTargetsAvailablePeriods({}));
    },
  });
});

export const guideTargetsSliceWebsocketActions: WebsocketEventActions[] = [
  {
    type: GuideTargetsUpdatedTypes.GUIDE_TARGETS_UPDATED,
    action: guideTargetsUpdatedFromSocket,
    channel: 'baseSocket',
  },
];

export const guideTargetsAppSelectors = {
  useSelectItems: () => useAppSelector(selectGuideTargetsItems),
  useSelectCount: () => useAppSelector(selectGuideTargetsCount),
  useSelectStatus: () => useAppSelector(selectGuideTargetsStatus),
  useSelectServiceAreaKeys: () => useAppSelector(selectOnlyScServiceAreaKeys),
  useSelectFilters: () => useAppSelector(selectGuideTargetsFiltersState),
  useSelectSelectedYearMo: () =>
    useAppSelector(selectGuideTargetsSelectedYearMo),
  useSelectSideNavClientId: () =>
    useAppSelector(selectGuideTargetsSideNavClientId),
  useSelectTotalQueued: () => useAppSelector(selectGuideTargetsTotalQueued),
  useSelectTotalsByTargetType: () =>
    useAppSelector(selectGuideTargetsTotalsByTargetType),
  useSelectAvailablePeriodsStatus: () =>
    useAppSelector(selectAvailablePeriodsStatus),
  useSelectAvailableYearMoValues: () =>
    useAppSelector(selectAvailableYearMoValues),
  useSelectTargetsUpdated: () =>
    useAppSelector(selectGuideTargetsTargetsUpdated),
  useSelectTargetTypes: () => useAppSelector(selectTargetTypes),
  useSelectTargetTypeKeys: () => useAppSelector(selectTargetTypeKeys),
  useSelectUnAllocatedRevenue: () => useAppSelector(selectUnAllocatedRevenue),
  useSelectCurrentClientRevenue: () =>
    useAppSelector(selectCurrentClientRevenue),
};
